import {renderMath} from '../my_katex.js';
import {studApi, EVENT_TYPE} from './_studApi.js';
import {Websocket} from './_websockets.js';

let startedWebsocket = null;
const testInstanceTaskIdTo = new Map();

function updater(resp) {
    const {test_instance_task_id, answer} = resp;
    // {'test_instance_task_id': test_instance_task_id, 'answer': answer}
    const recievers = testInstanceTaskIdTo.get(test_instance_task_id);
    if (recievers) {
        recievers.forEach((reciever) => reciever(answer));
    }
}

function convertUtcToLocalTime(text) {
    const timeRegex = /\b(\d{2}):(\d{2})\b/g;
    const timezoneRegex = /\bUTC\b/g;

    // Получаем смещение в минутах и формируем строку часового пояса
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absOffset = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const minutes = String(absOffset % 60).padStart(2, "0");
    const timezoneString = `${sign}${hours}:${minutes} ${userTimeZone}`;

    // Заменяем время на локальное
    const updatedText = text.replace(timeRegex, (match, hh, mm) => {
        const utcDate = new Date();
        utcDate.setUTCHours(parseInt(hh, 10), parseInt(mm, 10), 0);

        return utcDate.toLocaleTimeString("default", {
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23"
        });
    });

    // Заменяем "UTC" на локальный часовой пояс
    return updatedText.replace(timezoneRegex, timezoneString);
}


export class ProblemChat {
    constructor(container, token, testInstanceTaskId) {
        this.container = container;
        this.token = token;
        this.testInstanceTaskId = testInstanceTaskId;
        this.chatOpen = false;
        this.messages = [];
        this.lastMessageTime = 0;
        this.init();
        this.addMessage("Support", convertUtcToLocalTime("Ask questions about this problem and get a response within 30 minutes (exam periods and weekdays 08:00–17:00 UTC)."));
        if (!testInstanceTaskIdTo.has(this.testInstanceTaskId)) {
            testInstanceTaskIdTo.set(this.testInstanceTaskId, new Set());
        }
        testInstanceTaskIdTo.get(this.testInstanceTaskId).add(this.receiveMessage.bind(this));
    }

    /**
     * Инициализация компонента: рендеринг и обработчики событий.
     */
    init() {
        this.render();
        this.get_chat_history();
        this.attachEventListeners();
    }

    async get_chat_history() {
        const msgs = (await studApi.get_chat_history(this.testInstanceTaskId))?.msgs || [];
        if (msgs.length > 0) {
            this.chatToggleButton.classList.add("chat-has-messages");
            this.chatNotificationIndicator.classList.remove("hidden");
        }
        for (let msg of msgs) {
            this.addMessage(msg.sender, msg.text);
        }
    }

    /**
     * Рендерит HTML структуру чата внутри контейнера.
     */
    render() {
        this.container.innerHTML = `
            <button class="chat-toggle-button" title="Ask a question on the problem">
                ?
                <span class="chat-notification-indicator hidden"></span>
            </button>
            <div class="chat-box hidden">
                <div class="chat-header">
                    <span>Support Chat</span>
                    <button class="chat-close-button">&times;</button>
                </div>
                <div class="chat-messages"></div>
                <div class="chat-input-container">
                    <textarea class="chat-input" placeholder="Type your question..."></textarea>
                    <button class="chat-send-button">Send</button>
                </div>
                <div class="chat-loader hidden"></div>
            </div>
        `;

        this.chatToggleButton = this.container.querySelector(".chat-toggle-button");
        this.chatNotificationIndicator = this.container.querySelector(".chat-notification-indicator");
        this.chatBox = this.container.querySelector(".chat-box");
        this.chatHeader = this.container.querySelector(".chat-header");
        this.chatMessages = this.container.querySelector(".chat-messages");
        this.chatInput = this.container.querySelector(".chat-input");
        this.chatSendButton = this.container.querySelector(".chat-send-button");
        this.chatCloseButton = this.container.querySelector(".chat-close-button");
        this.chatLoader = this.container.querySelector(".chat-loader");
    }

    /**
     * Добавляет обработчики событий.
     */
    attachEventListeners() {
        this.chatToggleButton.addEventListener("click", () => this.toggleChat());
        this.chatCloseButton.addEventListener("click", () => this.closeChat());
        this.chatSendButton.addEventListener("click", () => this.sendMessage());
        this.chatInput.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                if (event.ctrlKey || event.metaKey) {
                    event.preventDefault();
                    this.sendMessage();
                } else {
                    event.preventDefault();
                    const start = this.chatInput.selectionStart;
                    const end = this.chatInput.selectionEnd;
                    this.chatInput.value =
                        this.chatInput.value.substring(0, start) + "\n" + this.chatInput.value.substring(end);
                    this.chatInput.selectionStart = this.chatInput.selectionEnd = start + 1;
                }
            }
        });
    }

    /**
     * Открывает/закрывает чат.
     */
    toggleChat() {
        this.chatOpen = !this.chatOpen;
        this.chatBox.classList.toggle("hidden", !this.chatOpen);
        this.chatToggleButton.classList.toggle("hidden", this.chatOpen);
    }

    openThisChat() {
        this.chatOpen = !this.chatOpen;
        this.chatBox.classList.toggle("hidden", false);
        this.chatToggleButton.classList.toggle("hidden", true);
    }
    /**
     * Закрывает чат.
     */
    closeChat() {
        this.chatOpen = false;
        this.chatBox.classList.add("hidden");
        this.chatToggleButton.classList.remove("hidden");
    }

    /**
     * Отправляет сообщение в поддержку.
     */
    async sendMessage() {
        const now = Date.now();
        if (now - this.lastMessageTime < 5000) {
            this.addMessage("System", "Please wait before sending another message.");
            return;
        }

        const question = this.chatInput.value.trim();
        if (!question) return;

        this.lastMessageTime = now;
        this.chatInput.value = "";
        this.showLoader(); // Показываем лоадер перед отправкой
        if (startedWebsocket === null) {
            startedWebsocket = new Websocket(this.token, updater);
        }
        try {
            await studApi.ask_question(this.testInstanceTaskId, question);
            this.addMessage("You", question);
        } catch (error) {
            this.addMessage("System", "Failed to send message. Please try again.");
            console.error(error);
        } finally {

            this.hideLoader(); // Скрываем лоадер после завершения
        }
        await startedWebsocket.connectionEstablishedPromise;
    }
    showLoader() {
        this.chatLoader.classList.add("visible");
    }

    hideLoader() {
        this.chatLoader.classList.remove("visible");
    }


    /**
     * Добавляет сообщение в чат.
     */
    addMessage(sender, text) {
        const messageElement = document.createElement("div");
        messageElement.className = sender === "You" ? "chat-message user" : "chat-message support";
        messageElement.innerHTML = `<strong>${sender}:</strong> ${text}`;
        renderMath(messageElement);
        this.chatMessages.appendChild(messageElement);
        this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
        return messageElement;
    }

    /**
     * Обрабатывает входящие сообщения из WebSocket.
     */
    receiveMessage(response) {
        console.log({response});
        studApi.test_event(EVENT_TYPE.SUPPORT_ANSWER, this.testInstanceTaskId, response);
        this.openThisChat();
        const messageElement = this.addMessage("Support", response);
        this.chatHeader.scrollIntoView({ behavior: "smooth", block: "center" });
        messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
}
